import type { RouteLeg, RoutePlan, RoutePlanStop } from '@/types/route-plan';
import { RoutePlanStore } from '@/lib/route-plan/RoutePlanStore';
import type { RouteStopCandidate } from '@/lib/route-plan/RouteStopCandidateBuilder';

export const HOME_STOP_TITLE = 'Home';

/**
 * RouteDraftFactory converts wizard state (selected candidates + home
 * address) into the RoutePlanStop list sent to the build endpoint and the
 * final RoutePlan persisted on save.
 */
export class RouteDraftFactory {
  /**
   * Selected candidates + home → RoutePlanStop[] (home stop first).
   */
  static buildStops(
    candidates: RouteStopCandidate[],
    homeAddress: string,
  ): RoutePlanStop[] {
    const homeStop: RoutePlanStop = {
      id: RoutePlanStore.generateId('stop'),
      source: 'home',
      title: HOME_STOP_TITLE,
      location: homeAddress.trim(),
      familyMemberIds: [],
    };

    const stops = candidates
      .filter((candidate) => candidate.selected)
      .map((candidate): RoutePlanStop => ({
        id: candidate.id,
        source: candidate.source,
        ...(candidate.sourceId ? { sourceId: candidate.sourceId } : {}),
        title: candidate.title,
        ...(candidate.location.trim() ? { location: candidate.location.trim() } : {}),
        ...(candidate.fixedStart ? { fixedStart: candidate.fixedStart } : {}),
        ...(candidate.fixedEnd ? { fixedEnd: candidate.fixedEnd } : {}),
        ...(candidate.estimatedDurationMinutes
          ? { estimatedDurationMinutes: candidate.estimatedDurationMinutes }
          : {}),
        familyMemberIds: candidate.familyMemberIds,
        ...(candidate.sourceQuote ? { sourceQuote: candidate.sourceQuote } : {}),
      }));

    return [homeStop, ...stops];
  }

  /**
   * Assemble the plan persisted on save.
   */
  static buildPlan(params: {
    date: string;
    windowStart: string | null;
    windowEnd: string | null;
    stops: RoutePlanStop[];
    legs: RouteLeg[];
  }): RoutePlan {
    const now = new Date().toISOString();
    return {
      id: RoutePlanStore.generateId('route'),
      date: params.date,
      ...(params.windowStart ? { windowStart: params.windowStart } : {}),
      ...(params.windowEnd ? { windowEnd: params.windowEnd } : {}),
      stops: params.stops,
      legs: params.legs,
      completedLegIds: [],
      status: 'active',
      createdAt: now,
      updatedAt: now,
    };
  }
}
